/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  ExternalLink,
  Share2,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUser } from '../../../contexts/UserContext';
import { URLS } from '../../../constant/constant';

// --- DATA ---
const socialPlatforms = [
  {
    name: 'Facebook',
    icon: Facebook,
    url: 'https://www.facebook.com/profile.php?id=61575943331303',
    description: 'Updates and community discussions.',
    color: 'hover:border-blue-600/50',
    iconColor: 'text-blue-600',
    followers: '2.5K Followers',
  },
  {
    name: 'Instagram',
    icon: Instagram,
    url: 'https://www.instagram.com/biglocal_in?igsh=MnpqZGNrcjNveGoy&utm_source=qr',
    description: 'Our latest photos and stories.',
    color: 'hover:border-pink-500/50',
    iconColor: 'text-pink-500',
    followers: '1.8K Followers',
  },
  {
    name: 'Twitter (X)',
    icon: Twitter,
    url: 'https://x.com/biglocal_in?s=11&t=kiWAF9DPzSDHAz67znvIbw',
    description: 'Real-time updates and news.',
    color: 'hover:border-sky-500/50',
    iconColor: 'text-sky-500',
    followers: '892 Followers',
  },
  {
    name: 'LinkedIn',
    icon: Linkedin,
    url: 'https://www.linkedin.com/company/biglocal-in/posts/?feedView=all',
    description: 'Connect with us professionally.',
    color: 'hover:border-blue-700/50',
    iconColor: 'text-blue-700',
    followers: '1.2K Followers',
  },
  {
    name: 'YouTube',
    icon: Youtube,
    url: 'https://youtube.com/@biglocal-t8w?si=cnUvLXvxs766To6w',
    description: 'Watch our videos and tutorials.',
    color: 'hover:border-red-600/50',
    iconColor: 'text-red-600',
    followers: '3.1K Subscribers',
  },
];

// --- SUB-COMPONENTS for a cleaner structure ---

const SocialCard = ({ platform }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0 },
    }}
    whileHover={{ y: -5, transition: { duration: 0.2 } }}
    className="bg-white rounded-2xl border border-slate-200/80 shadow-sm transition-all duration-300 h-full flex flex-col"
  >
    <div className="p-5 flex-grow">
      <div className="flex items-center gap-4 mb-3">
        <div className={`p-2.5 rounded-lg bg-slate-100`}>
          <platform.icon className={`h-6 w-6 ${platform.iconColor}`} />
        </div>
        <div>
          <h4 className="font-bold text-slate-900">{platform.name}</h4>
          <p className="text-xs text-slate-500 font-medium">{platform.followers}</p>
        </div>
      </div>
      <p className="text-sm text-slate-600">{platform.description}</p>
    </div>
    <div className="px-5 pb-5 mt-auto">
      <Button
        onClick={() => window.open(platform.url, '_blank', 'noopener,noreferrer')}
        variant="outline"
        className="w-full group bg-slate-50 hover:bg-slate-100 border-slate-200"
      >
        Follow{' '}
        <ExternalLink className="h-4 w-4 ml-2 opacity-50 group-hover:opacity-100 transition-opacity" />
      </Button>
    </div>
  </motion.div>
);

const CopiedToast = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 20 }}
    className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-full shadow-lg flex items-center gap-2"
  >
    <Check className="w-4 h-4 text-green-400" /> Link Copied to Clipboard!
  </motion.div>
);

// --- MAIN COMPONENT ---
const ProfileSocial = () => {
  const { user } = useUser();
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  const handleShare = async () => {
    const inviteCode = user.contactNo?.replace('+91', '') || '';
    const url = `${URLS.USER}/vendor/profile?mobile=${inviteCode}`;

    const text = `Discover *${user?.shopName}'s product and services now on Biglocal.in!* We are your local seller, online and ready to serve you. Support neighborhood businesses and *Go Local—it makes a difference!* Explore my profile here.\n
👇 Tap below to explore & order:\n${url}`;

    const shareData = {
      title: "BigLocal - India's Local App",
      text,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Modern fallback using clipboard API with a toast notification
      navigator.clipboard.writeText(shareData.url);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 2000); // Hide after 2 seconds
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}

      <main className="max-w-8xl mx-auto pb-20 pt-6 px-4 space-y-10">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center p-8 bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl border border-slate-200/80"
        >
          <Share2 className="h-10 w-10 mx-auto mb-4 text-green-600" />
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Spread the Word</h2>
          <p className="text-slate-600 max-w-lg mx-auto mb-6">
            Love supporting local? Share the BigLocal app with your friends and family and help us
            make local big!
          </p>
          <Button
            onClick={handleShare}
            className="bg-green-600 hover:bg-green-700 text-white font-bold px-6 py-3 text-base rounded-full shadow-lg shadow-green-500/20 hover:shadow-xl hover:shadow-green-500/30 transition-all transform hover:scale-105"
          >
            <Share2 className="h-5 w-5 mr-2" />
            Share the App
          </Button>
        </motion.div>

        {/* Social Platforms Grid */}
        <div>
          <h3 className="text-xl font-bold text-slate-900 mb-5 text-center sm:text-left">
            Follow Us On Social Media
          </h3>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            variants={{
              visible: { transition: { staggerChildren: 0.05 } },
            }}
            initial="hidden"
            animate="visible"
          >
            {socialPlatforms.map((platform) => (
              <SocialCard key={platform.name} platform={platform} />
            ))}
          </motion.div>
        </div>
      </main>

      <AnimatePresence>{showCopiedToast && <CopiedToast />}</AnimatePresence>
    </div>
  );
};

export default ProfileSocial;
